import type { QueryClient } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { playLeaveSound } from '#features/voice/utils/roomSounds';
import { useVoiceStore } from '#features/voice/model/voiceStore';

export type VoiceParticipantPayload = {
  userId: string;
  /** Сейчас в голосе. Это ответ медиасервера, а не наша запись: закрывший вкладку исчезает мгновенно. */
  connected: boolean;
  /** Автор поста — он же модератор комнаты. */
  host: boolean;
  totalSeconds: number;
};

export type VoiceRoomPayload = {
  sessionId: string;
  postId: string;
  /** OPEN | CLOSED */
  status: string;
  hostId: string | null;
  maxParticipants: number | null;
  connectedCount: number;
  participants: VoiceParticipantPayload[];
  /** Заполняются только в ответе на join и только тому, кто входит. */
  livekitUrl: string | null;
  token: string | null;
};

/**
 * Код отказа с бэкенда сохраняется как есть: voice.not.member, voice.room.full,
 * voice.capacity.reached и остальные. Текст к ним уже локализован на сервере, а код нужен, чтобы
 * интерфейс мог отличить временную занятость от постоянного запрета.
 */
export class VoiceRequestError extends Error {
  readonly errorCode: string;

  readonly errorMessage: string | null;

  constructor(errorCode: string, errorMessage?: string | null) {
    super(errorMessage || errorCode);
    this.errorCode = errorCode;
    this.errorMessage = errorMessage ?? null;
  }
}

const VOICE_ROOM_FIELDS = `
  sessionId
  postId
  status
  hostId
  maxParticipants
  connectedCount
  participants {
    userId
    connected
    host
    totalSeconds
  }
`;

const joinVoiceDoc = graphql(`
  mutation VoiceJoin($sessionId: UUID!) {
    voice {
      join(sessionId: $sessionId) {
        __typename
        ... on VoiceRoom {
          sessionId
          postId
          status
          hostId
          maxParticipants
          connectedCount
          participants {
            userId
            connected
            host
            totalSeconds
          }
          livekitUrl
          token
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        # У InputError нет errorCode/errorMessage — только список полей. Выбрать несуществующее
        # поле здесь значит уронить мутацию целиком на валидации, то есть закрыть вход в голос.
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`);

const leaveVoiceDoc = graphql(`
  mutation VoiceLeave($sessionId: UUID!) {
    voice {
      leave(sessionId: $sessionId) {
        __typename
        ... on VoiceRoom {
          sessionId
          postId
          status
          connectedCount
          participants {
            userId
            connected
            host
            totalSeconds
          }
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const kickVoiceDoc = graphql(`
  mutation VoiceKick($sessionId: UUID!, $userId: UUID!) {
    voice {
      kick(sessionId: $sessionId, userId: $userId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const muteVoiceDoc = graphql(`
  mutation VoiceMute($sessionId: UUID!, $userId: UUID!) {
    voice {
      mute(sessionId: $sessionId, userId: $userId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

// Строкой, а не через graphql(): `voice.room` возвращает голый тип без union — тот же приём, что
// у активного звонка в соседней папке.
const voiceRoomQuery = `
  query VoiceRoom($sessionId: UUID!) {
    voice {
      room(sessionId: $sessionId) {
        ${VOICE_ROOM_FIELDS}
      }
    }
  }
`;

type VoiceUnionPayload =
  | ({ __typename: 'VoiceRoom' } & VoiceRoomPayload)
  | { __typename: 'GlobalError'; errorCode?: string | null; errorMessage?: string | null }
  | { __typename: 'InputError'; errors?: { name?: string | null; messagesErrors?: string[] | null }[] | null }
  | { __typename?: string };

const unwrapRoom = (payload: VoiceUnionPayload | null | undefined): VoiceRoomPayload => {
  if (!payload || payload.__typename !== 'VoiceRoom') {
    if (payload?.__typename === 'InputError') {
      // У InputError код отказа не приходит вовсе — берём первое сообщение поля, чтобы человек
      // увидел причину, а не безымянную ошибку.
      const fields = (payload as { errors?: { messagesErrors?: string[] | null }[] | null }).errors;
      const first = fields?.[0];
      throw new VoiceRequestError('voice.input.error', first?.messagesErrors?.[0] ?? null);
    }
    const error = payload as { errorCode?: string | null; errorMessage?: string | null } | undefined;
    throw new VoiceRequestError(error?.errorCode ?? 'global.error', error?.errorMessage);
  }
  const room = payload as { __typename: 'VoiceRoom' } & VoiceRoomPayload;
  return { ...room, participants: room.participants ?? [] };
};

const assertOperationOk = (payload: { __typename?: string; errorCode?: string | null; errorMessage?: string | null } | null | undefined) => {
  if (!payload || payload.__typename !== 'Operation') {
    throw new VoiceRequestError(payload?.errorCode ?? 'global.error', payload?.errorMessage);
  }
};

/** Вход в голос сессии. Возвращает учётные данные LiveKit — они персональные, не кэшировать. */
export const joinVoiceRoom = async (sessionId: string): Promise<VoiceRoomPayload> => {
  const data = (await gqlFetcher(joinVoiceDoc, { sessionId })) as {
    voice?: { join?: VoiceUnionPayload | null } | null;
  };
  return unwrapRoom(data.voice?.join);
};

/**
 * Явный выход. Медиа рвёт сам клиент; этот вызов нужен, чтобы остальные увидели уход сразу, не
 * дожидаясь серверной сверки.
 */
export const leaveVoiceRoom = async (sessionId: string): Promise<VoiceRoomPayload | null> => {
  const data = (await gqlFetcher(leaveVoiceDoc, { sessionId })) as {
    voice?: { leave?: VoiceUnionPayload | null } | null;
  };
  const payload = data.voice?.leave;
  // Выход из уже закрытой комнаты — не ошибка: её могла закрыть сверка, пока мы отключались.
  if (!payload || payload.__typename !== 'VoiceRoom') {
    return null;
  }
  return unwrapRoom(payload);
};

export const kickFromVoiceRoom = async (sessionId: string, userId: string): Promise<void> => {
  const data = (await gqlFetcher(kickVoiceDoc, { sessionId, userId })) as {
    voice?: { kick?: { __typename?: string; errorCode?: string | null } | null } | null;
  };
  assertOperationOk(data.voice?.kick);
};

export const muteInVoiceRoom = async (sessionId: string, userId: string): Promise<void> => {
  const data = (await gqlFetcher(muteVoiceDoc, { sessionId, userId })) as {
    voice?: { mute?: { __typename?: string; errorCode?: string | null } | null } | null;
  };
  assertOperationOk(data.voice?.mute);
};

/** Состояние комнаты без учётных данных — для карточки сессии и списков. */
export const fetchVoiceRoom = async (sessionId: string): Promise<VoiceRoomPayload | null> => {
  const data = (await gqlFetcher(voiceRoomQuery, { sessionId })) as {
    voice?: { room?: VoiceRoomPayload | null } | null;
  };
  const room = data.voice?.room;
  return room ? { ...room, participants: room.participants ?? [] } : null;
};

/**
 * Выход из голоса откуда угодно: из виджета, из карточки сессии, из отмены подключения.
 *
 * Порядок обязателен — сперва гасим стор, и только потом сообщаем серверу. Сброс стора роняет
 * эффект, который держит соединение с медиасервером, то есть человек перестаёт быть слышен
 * мгновенно; сетевой запрос после этого лишь ускоряет обновление у остальных.
 *
 * Клиент кэша передаётся снаружи: в приложении их два — свой создаёт роутер (он и стоит за
 * useQuery в карточках), а модульный синглтон живёт сам по себе. Запись в синглтон уходила в
 * никуда, и после «выйти» карточка держала прежнее «N в голосе» до перезагрузки страницы.
 */
export const leaveVoice = async (client: QueryClient) => {
  const sessionId = useVoiceStore.getState().room?.sessionId;
  useVoiceStore.getState().reset();
  if (!sessionId) {
    return;
  }
  // Сигнал ставится здесь, а не на кнопке: из голоса выходят пятью разными путями, включая
  // автоматический — когда человека вынимает входящий звонок. Условие про `sessionId` выше и
  // отделяет настоящий уход из комнаты от выхода из аккаунта, когда в голосе никого не было.
  void playLeaveSound();
  const room = await leaveVoiceRoom(sessionId).catch(() => null);
  // Ответ на выход несёт свежий состав — кладём его в кэш сразу. Иначе карточка сессии ещё
  // несколько секунд показывает «N в голосе», считая туда только что вышедшего.
  if (room) {
    client.setQueryData(voiceRoomQueryKey(sessionId), room);
  } else {
    await client.invalidateQueries({ queryKey: voiceRoomQueryKey(sessionId) });
  }
};

export const voiceRoomQueryKey = (sessionId: string) => ['voiceRoom', sessionId] as const;

export const getVoiceRoomQueryOptions = (sessionId: string) => ({
  queryKey: voiceRoomQueryKey(sessionId),
  queryFn: () => fetchVoiceRoom(sessionId),
  // Состояние комнаты приезжает по WebSocket, поэтому опрос не нужен; короткая свежесть спасает
  // лишь от гонки между входом и первым кадром события.
  staleTime: 5_000,
});
